import { ApplicationConfig, provideZoneChangeDetection } from '@angular/core';
import { provideRouter } from '@angular/router';
import { appRoutes } from './app.routes';
import {
  provideHttpClient,
  withInterceptorsFromDi,
  HTTP_INTERCEPTORS,
} from '@angular/common/http';
import { provideAnimations } from '@angular/platform-browser/animations';
import { en_US, provideNzI18n } from 'ng-zorro-antd/i18n';
import { provideNzIcons } from 'ng-zorro-antd/icon';
import { registerLocaleData } from '@angular/common';
import en from '@angular/common/locales/en';
import { AuthInterceptor } from '@hypenation/auth';
import { ErrorInterceptor } from '@hypenation/shared';
import {
  CHECKOUT_PROFILE_PROVIDER,
  CheckoutProfile,
  CheckoutProfileProvider,
} from '@hypenation/tickets';
import { CustomerProfileService } from '@hypenation/client-portal';
import { computed } from '@angular/core';

import {
  MenuOutline,
  MenuFoldOutline,
  MenuUnfoldOutline,
  AppstoreOutline,
  ShopOutline,
  ShoppingOutline,
  QrcodeOutline,
  CheckSquareOutline,
  HeartOutline,
  TagOutline,
  ExportOutline,
  ShoppingCartOutline,
  EnvironmentOutline,
  DollarOutline,
  CalendarOutline,
  HistoryOutline,
  PlusOutline,
  RightOutline,
  ArrowLeftOutline,
  DeleteOutline,
  EyeOutline,
  InboxOutline,
  PictureOutline,
  LogoutOutline,
  CloseOutline,
  DownloadOutline,
  HeartFill,
  ClockCircleOutline,
  TeamOutline,
  MailOutline,
  PhoneOutline,
  GlobalOutline,
  InstagramOutline,
  FacebookOutline,
  WalletOutline,
  ShareAltOutline,
} from '@ant-design/icons-angular/icons';

registerLocaleData(en);

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(appRoutes),
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimations(),
    provideNzI18n(en_US),
    provideNzIcons([
      MenuOutline,
      MenuFoldOutline,
      MenuUnfoldOutline,
      AppstoreOutline,
      ShopOutline,
      ShoppingOutline,
      QrcodeOutline,
      CheckSquareOutline,
      HeartOutline,
      TagOutline,
      ExportOutline,
      ShoppingCartOutline,
      EnvironmentOutline,
      DollarOutline,
      CalendarOutline,
      HistoryOutline,
      PlusOutline,
      RightOutline,
      ArrowLeftOutline,
      DeleteOutline,
      EyeOutline,
      InboxOutline,
      PictureOutline,
      LogoutOutline,
      CloseOutline,
      DownloadOutline,
      HeartFill,
      ClockCircleOutline,
      TeamOutline,
      MailOutline,
      PhoneOutline,
      GlobalOutline,
      InstagramOutline,
      FacebookOutline,
      WalletOutline,
      ShareAltOutline,
    ]),
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    {
      provide: CHECKOUT_PROFILE_PROVIDER,
      useFactory: (svc: CustomerProfileService): CheckoutProfileProvider => ({
        profile: computed<CheckoutProfile | null>(() => {
          const c = svc.profile();
          if (!c) return null;

          // The Customer record only stores a single `name` field, but the
          // attendee forms typically have separate firstName/lastName
          // fields. Split here so AddToCartComponent's flat-profile
          // matcher can pick up either shape.
          const fullName = (c.name ?? '').trim();
          const parts = fullName.length ? fullName.split(/\s+/) : [];
          const firstName = parts[0] ?? '';
          const lastName = parts.length > 1 ? parts.slice(1).join(' ') : '';

          return {
            name: fullName || undefined,
            firstName: firstName || undefined,
            lastName: lastName || undefined,
            email: c.email,
            phoneNumber: c.phoneNumber,
          };
        }),
      }),
      deps: [CustomerProfileService],
    },
  ],
};
